/* eslint-disable code-style/no-hardcoded-strings */

import { lazy } from "react";
import { createBrowserRouter } from "react-router";

import { ErrorBoundary } from "@/components";
import { routesUrlsData } from "@/data";
import { RootLayout } from "@/layouts";

import { ProtectedRoute } from "./protected-route";

const AdminsCreateView = lazy(async () => {
    const module = await import("@/views/dashboard/admins/create");

    return { default: module.CreateAdminDashboardView };
});

const AdminsListView = lazy(async () => {
    const module = await import("@/views/dashboard/admins");

    return { default: module.AdminsDashboardView };
});

const AdminsUpdateView = lazy(async () => {
    const module = await import("@/views/dashboard/admins/update");

    return { default: module.UpdateAdminDashboardView };
});

const DashboardView = lazy(async () => {
    const module = await import("@/views/dashboard");

    return { default: module.DashboardView };
});

const EventGuestPhotosListView = lazy(async () => {
    const module = await import("@/views/dashboard/events/guests/guest-photos");

    return { default: module.GuestPhotosGuestEventDashboardView };
});

const EventGuestsListView = lazy(async () => {
    const module = await import("@/views/dashboard/events/guests");

    return { default: module.GuestsEventsDashboardView };
});

const HeadshotGuestsListView = lazy(async () => {
    const module = await import("@/views/dashboard/events/headshot-guests");

    return { default: module.HeadshotGuestsEventsDashboardView };
});

const EventPhotosListView = lazy(async () => {
    const module = await import("@/views/dashboard/events/photos");

    return { default: module.PhotosEventsDashboardView };
});

const EventsCreateView = lazy(async () => {
    const module = await import("@/views/dashboard/events/create");

    return { default: module.CreateEventsDashboardView };
});

const EventSlideshowListView = lazy(async () => {
    const module = await import("@/views/dashboard/events/slideshow");

    return { default: module.SlideshowEventsDashboardView };
});

const EventsListView = lazy(async () => {
    const module = await import("@/views/dashboard/events/index");

    return { default: module.IndexEventsDashboardView };
});

const EventsUpdateView = lazy(async () => {
    const module = await import("@/views/dashboard/events/update");

    return { default: module.UpdateEventsDashboardView };
});

const EventTemplatesCreateView = lazy(async () => {
    const module = await import("@/views/dashboard/events/templates/create");

    return { default: module.EventTemplatesCreateView };
});

const EventTemplatesListView = lazy(async () => {
    const module = await import("@/views/dashboard/events/templates");

    return { default: module.TemplatesEventsDashboardView };
});

const EventTemplatesUpdateView = lazy(async () => {
    const module = await import("@/views/dashboard/events/templates/update");

    return { default: module.EventTemplatesUpdateView };
});

const LoginView = lazy(async () => {
    const module = await import("@/views/auth/login");

    return { default: module.LoginAuthView };
});

const NotFoundView = lazy(async () => {
    const module = await import("@/views/not-found");

    return { default: module.NotFoundView };
});

const PhotographersCreateView = lazy(async () => {
    const module = await import("@/views/dashboard/photographers/create");

    return { default: module.CreatePhotographerDashboardView };
});

const PhotographersListView = lazy(async () => {
    const module = await import("@/views/dashboard/photographers");

    return { default: module.PhotographersDashboardView };
});

const PhotographersUpdateView = lazy(async () => {
    const module = await import("@/views/dashboard/photographers/update");

    return { default: module.UpdatePhotographerDashboardView };
});

const SetPasswordView = lazy(async () => {
    const module = await import("@/views/auth/set-password");

    return { default: module.SetPasswordAuthView };
});

export const Routes = createBrowserRouter([
    {
        children: [
            {
                children: [
                    {
                        element: <LoginView />,
                        path: routesUrlsData.routeSegments.login,
                    },
                    {
                        element: <SetPasswordView />,
                        path: routesUrlsData.routeSegments.setPassword,
                    },
                ],
                path: routesUrlsData.routeSegments.auth,
            },
            {
                children: [
                    {
                        children: [
                            {
                                children: [
                                    {
                                        element: <AdminsListView />,
                                        index: true,
                                    },
                                    {
                                        element: <AdminsCreateView />,
                                        path: routesUrlsData.routeSegments.create,
                                    },
                                    {
                                        element: <AdminsUpdateView />,
                                        path: routesUrlsData.routeSegments.update,
                                    },
                                ],
                                path: routesUrlsData.routeSegments.admins,
                            },
                            {
                                children: [
                                    {
                                        element: <PhotographersListView />,
                                        index: true,
                                    },
                                    {
                                        element: <PhotographersCreateView />,
                                        path: routesUrlsData.routeSegments.create,
                                    },
                                    {
                                        element: <PhotographersUpdateView />,
                                        path: routesUrlsData.routeSegments.update,
                                    },
                                ],
                                path: routesUrlsData.routeSegments.photographers,
                            },
                            {
                                children: [
                                    {
                                        element: <EventsListView />,
                                        index: true,
                                    },
                                    {
                                        element: <EventGuestsListView />,
                                        path: routesUrlsData.routeSegments.guests,
                                    },
                                    {
                                        element: <HeadshotGuestsListView />,
                                        path: routesUrlsData.routeSegments.retouchQueue,
                                    },
                                    {
                                        element: <EventPhotosListView />,
                                        path: routesUrlsData.routeSegments.photos,
                                    },
                                    {
                                        element: <EventSlideshowListView />,
                                        path: routesUrlsData.routeSegments.slideshow,
                                    },
                                    {
                                        element: <EventGuestPhotosListView />,
                                        path: routesUrlsData.routeSegments.eventGuestPhotos,
                                    },
                                    {
                                        element: <EventTemplatesListView />,
                                        path: routesUrlsData.routeSegments.templatesIndex,
                                    },
                                    {
                                        element: <EventTemplatesCreateView />,
                                        path: routesUrlsData.routeSegments.templatesCreate,
                                    },
                                    {
                                        element: <EventTemplatesUpdateView />,
                                        path: routesUrlsData.routeSegments.templatesUpdate,
                                    },
                                    {
                                        element: <EventsCreateView />,
                                        path: routesUrlsData.routeSegments.create,
                                    },
                                    {
                                        element: <EventsUpdateView />,
                                        path: routesUrlsData.routeSegments.update,
                                    },
                                ],
                                path: routesUrlsData.routeSegments.events,
                            },
                        ],
                        element: <DashboardView />,
                        path: routesUrlsData.routeSegments.root,
                    },
                ],
                element: <ProtectedRoute />,
            },
            {
                element: <NotFoundView />,
                path: routesUrlsData.routeSegments.notFound,
            },
        ],
        element: <RootLayout />,
        errorElement: <ErrorBoundary />,
    },
]);
